import { Users } from "@/lib/users/types";

/** Fields that must be non-null and non-empty for the profile to be considered complete. */
export const REQUIRED_PROFILE_FIELDS = ["first_name", "last_name", "title", "phone"] as const;

/**
 * Profile-picture upload allowlist. Excludes SVG, which can carry executable script (pen-test
 * Finding 3 stored-XSS). Validated client-side via `isAllowedRasterImage`; the authoritative
 * control is the `profile_pictures` bucket `allowed_mime_types` (see
 * `migrations/20260605_finding3_storage_allowed_mime_types.sql`).
 */
export const SUPPORTED_PROFILE_PICTURE_MIME_TYPES = ["image/png", "image/jpeg"] as const;
export const SUPPORTED_PROFILE_PICTURE_EXTENSIONS = ["jpg", "jpeg", "png"] as const;

/** Accepted file extensions for profile picture upload (HTML `accept` attribute). */
export const ACCEPTED_IMAGE_TYPES = SUPPORTED_PROFILE_PICTURE_EXTENSIONS.map((ext) => `.${ext}`).join(",");

/** Maximum file size for profile pictures (2 MB). */
export const MAX_PROFILE_PICTURE_SIZE_BYTES = 2 * 1024 * 1024;

/** Default country dial code ID (United States). */
export const US_COUNTRY_ID = 235;

/** Maximum height of the country dropdown in pixels. */
export const DROPDOWN_MAX_HEIGHT = 360;

/** Minimum width of the country dropdown in pixels. */
export const DROPDOWN_MIN_WIDTH = 320;

/** Z-index for portal-based dropdowns (above FetchModal's 99999). */
export const DROPDOWN_Z_INDEX = 999999;

/**
 * Returns true if any required profile field is missing or empty.
 * @param user - The user object to check.
 * @returns `true` if any required field is missing or empty.
 */
export function isProfileIncomplete(user: Users): boolean {
  return REQUIRED_PROFILE_FIELDS.some((field) => {
    const value = user[field as keyof Users];
    return !value || (typeof value === "string" && value.trim() === "");
  });
}

/** Date formats a user can pick for date display and entry. Values are dayjs/antd format tokens. */
export const DATE_FORMATS = ["MM/DD/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"] as const;

/** One of {@link DATE_FORMATS}. */
export type DateFormat = (typeof DATE_FORMATS)[number];

/** Date format outside `DateFormatProvider`, and before it reads the browser locale. Matches the historical US-style display. */
export const DEFAULT_DATE_FORMAT: DateFormat = "MM/DD/YYYY";
